import React, { useId, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function TeacherPicker({ selected, onSelect, onSearch, onInvite, disabled = false }) {
  const listboxId = useId();
  const inviteNameRef = useRef(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searchStatus, setSearchStatus] = useState("idle");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteError, setInviteError] = useState("");
  const [inviteStatus, setInviteStatus] = useState("idle");
  const searchRequest = useRef(0);
  const trimmedQuery = query.trim();
  const inviteDisabled = inviteStatus === "sending" || disabled;

  const runSearch = async (value) => {
    const requestId = searchRequest.current + 1;
    searchRequest.current = requestId;
    setQuery(value);
    setInviteError("");
    if (value.trim().length < 2) {
      setResults([]);
      setSearchStatus("idle");
      return;
    }
    setSearchStatus("loading");
    try {
      const matches = await onSearch(value);
      if (searchRequest.current !== requestId) return;
      setResults(matches);
      setSearchStatus("ready");
    } catch (_) {
      if (searchRequest.current === requestId) setSearchStatus("error");
    }
  };

  const chooseTeacher = (teacher) => {
    onSelect(teacher);
    setQuery("");
    setResults([]);
    setSearchStatus("idle");
    setInviteOpen(false);
    setInviteStatus("idle");
  };

  const openInvite = (suggestedName = query) => {
    setInviteOpen(true);
    setInviteName((current) => current || suggestedName.trim());
    setInviteStatus("idle");
    setInviteError("");
    window.setTimeout(() => inviteNameRef.current?.focus(), 0);
  };

  const submitInvite = async (event) => {
    event?.preventDefault();
    const name = inviteName.trim();
    const email = inviteEmail.trim();
    if (!name) {
      setInviteError("Enter the teacher’s name.");
      inviteNameRef.current?.focus();
      return;
    }
    if (!emailPattern.test(email)) {
      setInviteError("Enter a valid teacher email address.");
      return;
    }
    setInviteStatus("sending");
    setInviteError("");
    try {
      const result = await onInvite({ name, email });
      chooseTeacher(result.teacher);
      setInviteStatus(result.existing ? "existing" : "sent");
    } catch (error) {
      setInviteStatus("failed");
      setInviteError(error?.message || "The invitation could not be sent. You can retry or choose another teacher.");
    }
  };

  const submitInviteOnEnter = (event) => {
    if (event.key === "Enter") submitInvite(event);
  };

  return (
    <div className="handover-teacher-picker">
      {selected ? (
        <>
          <div className="handover-selected-teacher-field">
            <FontAwesomeIcon icon={faSearch} className="handover-search-icon" aria-hidden="true" />
            <strong>{selected.name}</strong>
            <button type="button" aria-label="Choose a different teacher" onClick={() => onSelect(null)} disabled={disabled}>×</button>
          </div>
          <p className="handover-field-hint" role="status">
            {selected.kind === "pending" ? <><span>Invitation pending</span><span> — you can continue setup now.</span></> : "Teacher selected."}
          </p>
        </>
      ) : (
        <>
          <label htmlFor="handover-teacher-search">Search for an existing teacher in ALPACA.</label>
          <div className="handover-teacher-search-wrap">
            <FontAwesomeIcon icon={faSearch} className="handover-search-icon" aria-hidden="true" />
            <input
              id="handover-teacher-search"
              type="text"
              role="combobox"
              aria-controls={listboxId}
              aria-expanded={results.length > 0}
              aria-autocomplete="list"
              autoComplete="off"
              value={query}
              onChange={(event) => runSearch(event.target.value)}
              placeholder="Start typing a name or email…"
              disabled={disabled}
            />
            {query ? (
              <button className="handover-search-clear" type="button" aria-label="Clear teacher search" onClick={() => runSearch("")}>×</button>
            ) : <span className="handover-search-chevron" aria-hidden="true">⌄</span>}
          </div>
          {trimmedQuery.length < 2 && <p className="handover-field-hint">Type at least 2 characters to search.</p>}
          {searchStatus === "loading" && <p role="status">Searching teachers…</p>}
          {searchStatus === "error" && <p role="alert">Teacher search is unavailable. Try again.</p>}
          {searchStatus === "ready" && results.length === 0 && (
            <div className="handover-teacher-results handover-teacher-no-results" role="status">
              <strong>No teachers found.</strong>
              <span>Try a different name.</span>
              <button type="button" onClick={() => openInvite(query)}>+ Create a new teacher “{trimmedQuery}”</button>
            </div>
          )}
          {results.length > 0 && (
            <ul id={listboxId} role="listbox" className="handover-teacher-results">
              {results.map((teacher) => (
                <li key={`${teacher.kind}-${teacher.id}`} role="option" aria-selected="false">
                  <button type="button" onClick={() => chooseTeacher(teacher)}>
                    <span><strong>{teacher.name}</strong>{teacher.email && <small>{teacher.email}</small>}</span>
                    <span className={`handover-account-status handover-account-status--${teacher.kind}`}>
                      {teacher.kind === "pending" ? "Pending" : "Active"}
                    </span>
                  </button>
                </li>
              ))}
              <li className="handover-create-from-query">
                <button type="button" onClick={() => openInvite(query)}>+ Create a new teacher “{trimmedQuery}”</button>
              </li>
            </ul>
          )}
        </>
      )}

      {!selected && !inviteOpen && (
        <div className="handover-create-teacher-prompt">
          <span className="handover-create-teacher-divider" aria-hidden="true">or</span>
          <div>
            <strong>Can’t find the teacher?</strong>
            <span>Invite a new teacher to ALPACA.</span>
          </div>
          <button type="button" onClick={() => openInvite()} disabled={disabled}>+ Create a new teacher</button>
        </div>
      )}

      {!selected && inviteOpen && (
        <div className="handover-invite-form">
          <div className="handover-invite-intro">
            <strong>Create a new teacher in ALPACA</strong>
            <span>Enter the teacher’s details.</span>
          </div>
          <div className="handover-invite-fields">
            <div className="handover-invite-field">
              <label htmlFor="handover-invite-name">Teacher name</label>
              <input
                ref={inviteNameRef}
                id="handover-invite-name"
                value={inviteName}
                placeholder="e.g. Mary Murphy"
                onChange={(event) => setInviteName(event.target.value)}
                autoComplete="name"
                disabled={inviteDisabled}
                onKeyDown={submitInviteOnEnter}
              />
            </div>
            <div className="handover-invite-field">
              <label htmlFor="handover-invite-email">Teacher email</label>
              <input
                id="handover-invite-email"
                type="email"
                value={inviteEmail}
                placeholder="e.g. mary@school.ie"
                onChange={(event) => setInviteEmail(event.target.value)}
                autoComplete="email"
                disabled={inviteDisabled}
                onKeyDown={submitInviteOnEnter}
              />
            </div>
          </div>
          <p className="handover-invite-note">We’ll invite them to ALPACA so they can access their account. You can continue setting up the classroom now.</p>
          {inviteError && <p className="handover-field-error" role="alert">{inviteError}</p>}
          <div className="handover-invite-actions">
            <button type="button" onClick={() => setInviteOpen(false)} disabled={inviteStatus === "sending"}>Cancel</button>
            <button type="button" onClick={submitInvite} disabled={inviteDisabled}>
              {inviteStatus === "sending" ? "Checking…" : inviteStatus === "failed" ? "Retry email check" : "Check email"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeacherPicker;
